import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";

const RollingGameSettings = () => {
  const [rollHistory, setRollHistory] = useState(
    JSON.parse(localStorage.getItem("rollHistory")) || []
  );
  const [selectedDate, setSelectedDate] = useState(moment().startOf("day"));
  const [statsType, setStatsType] = useState("lifetime"); // "lifetime" or "day"
  const [stats, setStats] = useState({
    average: 0,
    highest: 0,
    lowest: 0,
    median: 0,
  });

  useEffect(() => {
    const storedData = localStorage.getItem("rollHistory");
    if (storedData) {
      setRollHistory(JSON.parse(storedData));
    }
  }, []);

  // ✅ Use useMemo to prevent unnecessary re-computation
  const filteredRolls = useMemo(() => {
    return rollHistory
      .filter((roll) => moment(roll.timestamp).isSame(selectedDate, "day"))
      .sort((a, b) => moment(b.timestamp).diff(moment(a.timestamp)));
  }, [rollHistory, selectedDate]);

  // ✅ Now we can safely include `filteredRolls` in useEffect
  useEffect(() => {
    const targetRolls = statsType === "lifetime" ? rollHistory : filteredRolls;

    if (targetRolls.length === 0) {
      setStats({ average: 0, highest: 0, lowest: 0, median: 0 });
      return;
    }

    const rollNumbers = targetRolls.map((roll) => roll.rollNumber);
    const sum = rollNumbers.reduce((a, b) => a + b, 0);
    const average = Math.round(sum / rollNumbers.length);
    const highest = Math.max(...rollNumbers);
    const lowest = Math.min(...rollNumbers);
    const sortedRolls = [...rollNumbers].sort((a, b) => a - b);
    const mid = Math.floor(sortedRolls.length / 2);
    const median =
      sortedRolls.length % 2 === 0
        ? (sortedRolls[mid - 1] + sortedRolls[mid]) / 2
        : sortedRolls[mid];

    setStats({ average, highest, lowest, median });
  }, [statsType, rollHistory, filteredRolls]);

  const getAuraColor = (roll) => {
    if (roll <= 3) return "bg-green-300";
    if (roll <= 10) return "bg-yellow-300";
    if (roll <= 15) return "bg-red-900";
    return "bg-blue-700";
  };

  const goToPreviousDay = () => {
    setSelectedDate((prev) => moment(prev).subtract(1, "day").startOf("day"));
  };

  const goToNextDay = () => {
    if (!selectedDate.isSame(moment().startOf("day"), "day")) {
      setSelectedDate((prev) => moment(prev).add(1, "day").startOf("day"));
    }
  };

  const goToToday = () => {
    setSelectedDate(moment().startOf("day"));
  };

  const goToFirstDay = () => {
    if (rollHistory.length === 0) return;
    const firstDate = moment(rollHistory[0].timestamp).startOf("day");
    setSelectedDate(firstDate);
  };

  return (
    <div>
      <div className="p-6 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl shadow-lg">
        <h2 className="text-3xl font-bold text-center mb-4">Rolls History</h2>

        <div className="text-center text-lg font-semibold mb-4">
          Showing rolls for:{" "}
          <span className="text-yellow-300">{selectedDate.format("LL")}</span>
        </div>

        {/* Stats Selection Buttons */}
        <div className="flex justify-center space-x-4 mb-4">
          <button
            onClick={() => setStatsType("lifetime")}
            className={`px-4 py-2 font-bold rounded-lg shadow transition ${
              statsType === "lifetime"
                ? "bg-green-500 text-white"
                : "bg-gray-600 text-gray-200"
            }`}
          >
            🌍 Lifetime Stats
          </button>
          <button
            onClick={() => setStatsType("day")}
            className={`px-4 py-2 font-bold rounded-lg shadow transition ${
              statsType === "day"
                ? "bg-blue-500 text-white"
                : "bg-gray-600 text-gray-200"
            }`}
          >
            📅 Day Stats
          </button>
        </div>

        {/* Stats Section */}
        <div className="flex justify-around text-lg font-semibold mb-4">
          <div className="bg-white/20 px-4 py-2 rounded-lg shadow">
            🎯 Average: <span className="text-yellow-300">{stats.average}</span>
          </div>
          <div className="bg-white/20 px-4 py-2 rounded-lg shadow">
            🚀 Highest: <span className="text-green-300">{stats.highest}</span>
          </div>
          <div className="bg-white/20 px-4 py-2 rounded-lg shadow">
            🛑 Lowest: <span className="text-red-300">{stats.lowest}</span>
          </div>
          <div className="bg-white/20 px-4 py-2 rounded-lg shadow">
            📊 Median: <span className="text-blue-300">{stats.median}</span>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-center space-x-4 mb-4">
          <button
            onClick={goToToday}
            className="px-4 py-2 bg-green-500 text-white font-bold rounded-lg shadow hover:bg-green-700 transition"
          >
            📅 Today
          </button>
          <button
            onClick={goToPreviousDay}
            className="px-4 py-2 bg-blue-500 text-white font-bold rounded-lg shadow hover:bg-blue-700 transition"
          >
            ◀ Previous
          </button>
          <button
            onClick={goToNextDay}
            disabled={selectedDate.isSame(moment().startOf("day"), "day")}
            className="px-4 py-2 bg-blue-500 text-white font-bold rounded-lg shadow hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Next ▶
          </button>
          <button
            onClick={goToFirstDay}
            className="px-4 py-2 bg-gray-600 text-white font-bold rounded-lg shadow hover:bg-gray-800 transition"
          >
            ⏮ First Day
          </button>
        </div>
        {/* Roll List */}
        <ul className="mt-4">
          {filteredRolls.length > 0 ? (
            filteredRolls.map((roll, index) => {
              const aura = getAuraColor(roll.rollNumber);
              return (
                <li
                  key={index}
                  className={`font-bold text-black text-xl ${aura} p-2 rounded mb-2`}
                >
                  {" "}
                  {filteredRolls.length - index} /{" "}
                  {moment(roll.timestamp).format("MMMM D, h:mm A")} :{" "}
                  {roll.rollNumber}
                </li>
              );
            })
          ) : (
            <p className="text-center text-gray-300">No rolls for this day.</p>
          )}
        </ul>
      </div>
    </div>
  );
};

export default RollingGameSettings;
